import BlokWrap from "@/components/atoms/BlokWrap";
import { ArrowRight } from "@/components/icons/ArrowRight";
import AspectRatioProvider from "@/components/modifiers/AspectRatioProvider";
import getLink from "@/lib/storyblok/utils/getLink";
import { FeaturedContentStoryblok } from "@/types/storyblok-blok-types";
import * as Dialog from "@radix-ui/react-dialog";
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import styles from "./FeaturedContent.module.css";

export const FeaturedContentBlok = ({
  blok,
  isCurated,
  closeDialog,
}: {
  blok: FeaturedContentStoryblok;
  isCurated?: boolean;
  closeDialog?: boolean;
}) => {
  const href = blok.link ? getLink(blok.link) : "#";

  if (isCurated) {
    let width = 338;
    let height = 507;

    switch (blok.size) {
      case "small":
        width = 306;
        height = 459;
        break;
      case "large":
        width = 386;
        height = 579;
        break;
      case "default":
      default:
        width = 338;
        height = 507;
        break;
    }

    return (
      <div
        className={clsx(styles.curatedSlide, styles.desktopOnly)}
        style={{ width }}
        {...storyblokEditable(blok as SbBlokData)}
      >
        {blok.image && (
          <Link
            href={href!}
            className={styles.curatedImageLink}
            style={{ width, height }}
          >
            <AspectRatioProvider width={2} height={3}>
              <div className={styles.curatedImageWrapper}>
                <Image
                  src={blok.image.filename}
                  alt={blok.image.alt || blok.title || "Featured"}
                  fill
                  quality={80}
                  className={styles.curatedImage}
                />
                {blok.description && (
                  <div className={styles.overlay}>
                    <p className={styles.descriptionHover}>
                      {blok.description}
                    </p>
                  </div>
                )}
              </div>
            </AspectRatioProvider>
          </Link>
        )}

        <div className={styles.titleRow}>
          {blok.title && (
            <Link href={href!} className={styles.curatedTitle}>
              {blok.title}
            </Link>
          )}
          <ArrowRight className={styles.arrow} />
        </div>
      </div>
    );
  }

  return (
    <BlokWrap
      {...storyblokEditable(blok as SbBlokData)}
      className={styles.root}
      animated="custom"
      data-layout="framed"
    >
      <div className={styles.featured}>
        {blok.image && (
          <div className={styles.image}>
            <Image
              src={blok.image.filename}
              alt={blok.image.alt || blok.title || "Featured"}
              fill
              style={{ objectFit: "cover" }}
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        )}
        <div className={styles.content}>
          {blok.title && <h2 className={styles.title}>{blok.title}</h2>}
          {blok.description && (
            <p className={styles.description}>{blok.description}</p>
          )}
          {blok.link &&
            (closeDialog ? (
              <Dialog.Close asChild>
                <Link href={href!} className={styles.link}>
                  {blok.title}
                </Link>
              </Dialog.Close>
            ) : (
              <Link href={href!} className={styles.link}>
                {blok.title}
              </Link>
            ))}
        </div>
      </div>
    </BlokWrap>
  );
};
